import React, { useContext, useState } from "react";
import * as Yup from "yup";

import { Field, Form, Formik } from "formik";

import ICONS from "../../constants/icons";
import { ModalContext } from "../../../context/ModalContext";

import styles from "./cancelSubscriptionModal.module.css";
import { useAppContext } from "../../../context/AppContext";
import Button from "../../button/Button";
import DeliveryDate from "../../deliveryDate/DeliveryDate";
import { useUser } from "../../../context/UserContext";
import { fetchPostJSON } from "../../../utils/api-helpers";
import moment from "moment";
import { OrderService } from "../../../services/order.service";
import { getDeliveryTypeForPostcode } from "../../../utils/orderHelpers";
import { $Enums } from "@prisma/client";
import { cancelSubscription } from "../../../modules/@stripe";

const reasons = [
  "Price is too high",
  "Not enough size availability",
  "Don’t find enough styles I like",
  "Moving outside of postal area",
  "Other",
];

const CancelSubscriptionModal = (): JSX.Element => {
  const { settings, postcodes } = useAppContext();
  const { handleModal } = useContext(ModalContext);
  const { userProfile, activeOrderProducts } = useUser();
  const [reason, setReason] = useState(null);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("8am-7pm");
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  if (!userProfile.activeOrder) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.modalLeft}>
        {message ? (
          <p className={styles.modalBodyCentered}>{message}</p>
        ) : (
          <div className={styles.description}>
            {!reason && (
              <div className={styles.modalTitle}>Cancel your subscription</div>
            )}
            {!reason ? (
              <div className={styles.modalBody}>
                <p>Why would you like to cancel your subscription?</p>

                <Formik
                  validationSchema={Yup.object().shape({
                    reason: Yup.string().required(),
                  })}
                  initialValues={{ reason: null }}
                  onSubmit={async (values) => {
                    setReason(values.reason);
                  }}
                >
                  {({ values }) => (
                    <Form>
                      <div role="group" aria-labelledby="my-radio-group">
                        {reasons.map((reas) => (
                          <label className={styles.label} key={`radio-${reas}`}>
                            <Field type="radio" name="reason" value={reas} />
                            {reas}
                          </label>
                        ))}
                      </div>

                      <Button className={styles.button} text="Cancel" />
                    </Form>
                  )}
                </Formik>
              </div>
            ) : (
              <div className={styles.modalBodyScroll}>
                <p>Please select a pickup date for your clothes</p>

                <DeliveryDate
                  inline
                  setDate={setDate}
                  currentDate={date}
                  deliveryType={getDeliveryTypeForPostcode(
                    userProfile.postcode,
                    postcodes
                  )}
                />

                <Button
                  className={styles.button}
                  disabled={isLoading}
                  onClick={async () => {
                    if (date) {
                      setIsLoading(true);
                      const momentDate = moment(date);

                      const twoDaysInAdvance = new Date();
                      twoDaysInAdvance.setDate(new Date().getDate() + 2);

                      if (momentDate.isBefore(twoDaysInAdvance)) {
                        alert(
                          "Please ensure your date selected is in the future"
                        );
                        setIsLoading(false);

                        return;
                      }

                      const timeString = moment(time, "HH:mm");
                      momentDate.set({
                        hour: timeString.get("hour"),
                        minute: timeString.get("minute"),
                        second: timeString.get("second"),
                      });
                      const formattedDate = momentDate.toDate();

                      await cancelSubscription({
                        order_id: userProfile.activeOrder.id,
                        user_id: userProfile.id,
                        cancellation_reason: reason,
                        date: momentDate.format(),
                      });

                      setIsLoading(false);

                      handleModal({ content: false, withImage: false });
                      setMessage(
                        "Subscription cancellation is being processed. We will be in touch with more information."
                      );
                    } else {
                      alert("please pick a date");
                    }

                    return true;
                  }}
                  text="Cancel Subscription"
                />
              </div>
            )}
          </div>
        )}

        {!reason && <ICONS.ORLOGO size={100} />}
      </div>

      <div
        className={styles.modalRight}
        style={{
          backgroundImage: `url(${settings?.data?.orderConfirmationPopupImage.url})`,
        }}
      ></div>
    </div>
  );
};

export default CancelSubscriptionModal;
